/**
 * BIK-2135: shared detectors for destinations UPS will not deliver to —
 * FedEx offices/drop boxes, USPS post offices, UPS Stores and PO/APO/FPO boxes.
 *
 * These used to live as private methods on ValidationDetails (Ship3) and read
 * only the `company` field. That is fine on Ship3 and on the Ship1 Google path
 * (Place sets company = POI name for `establishment` types), but it misses the
 * Ship1 paste/Region flow entirely: a one-line paste never sends a company to
 * UPS XAV, so nothing comes back to populate it, and updateDetails overwrites
 * address1/streetAddress with the parsed street line — dropping the POI name.
 * On that path the pasted text survives only in `rawInput`.
 *
 * So the detectors take a haystack of every field the name could be hiding in
 * and scan all of them.
 */

/** The subset of Details these guards read. */
export interface CarrierAddressHaystack {
  company?: string | null;
  rawInput?: string | null;
  streetAddress?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
}

/**
 * Lowercase, collapse whitespace and strip the punctuation that carrier names
 * are inconsistently written with, so "U.P.S. Store", "UPS  Store" and
 * "ups store" all normalize to the same needle-friendly string.
 */
function normalize(value?: string | null): string {
  if (typeof value !== "string" || !value) {
    return "";
  }
  return value
    .toLowerCase()
    .replace(/[.,]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Fields that carry a business/POI name: the company field, and the raw text
 * the user pasted (the only place the name survives on the Region path).
 * `city` is deliberately excluded — it is only used for the APO/FPO check,
 * where an exact match is required and a substring scan would false-positive.
 */
function poiHaystacks(address?: CarrierAddressHaystack | null): string[] {
  if (!address) {
    return [];
  }
  return [normalize(address.company), normalize(address.rawInput)].filter(
    (value) => value !== ""
  );
}

/**
 * Parsed street lines. Scanned for unambiguous brand names only — never for
 * generic phrases like "post office", which would falsely flag a street
 * legitimately named e.g. "44 Post Office Rd".
 */
function streetHaystacks(address?: CarrierAddressHaystack | null): string[] {
  if (!address) {
    return [];
  }
  return [
    normalize(address.streetAddress),
    normalize(address.address1),
    normalize(address.address2),
  ].filter((value) => value !== "");
}

function matchesAny(haystacks: string[], needles: string[]): boolean {
  return haystacks.some((haystack) =>
    needles.some((needle) => haystack.includes(needle))
  );
}

function startsWithAny(haystacks: string[], needles: string[]): boolean {
  return haystacks.some((haystack) =>
    needles.some((needle) => haystack.startsWith(needle))
  );
}

/** FedEx offices and drop boxes will not accept a UPS shipment. */
const FEDEX_NEEDLES = [
  "fedex",
  "fed ex",
  "federal express", // BIK-2135: long-form variant requested by Bill/Travis
];

/**
 * UPS Stores. A UPS Store IS a valid UPS destination, so this is intentionally
 * kept separate from the postal-service detector: shipping to one only warrants
 * a warning (call ahead), never a block.
 */
const UPS_STORE_NEEDLES = ["ups store", "the ups store", "ups access point"];

/**
 * USPS post offices, spelled out unambiguously. The misspelled singular
 * variants ("united state postal service") are kept on purpose — they were in
 * the original Ship3 list and tolerate a common typo — with the correct plural
 * forms added alongside.
 */
const POSTAL_SERVICE_NEEDLES = [
  "united state postal service",
  "united state postal office",
  "united states postal service",
  "united states postal office",
  "united states post office",
  "united state post office",
  "us post office",
  "us postal office",
  "us post service",
  "us postal service",
  "usps",
];

/**
 * Generic postal phrases, matched only at the START of company/rawInput.
 *
 * "Post Office Road" is a real street name in several US towns, so a substring
 * scan would block a deliverable house. A pasted one-liner puts the business
 * name first ("United States Postal Service, 105 DEPOT ST, ..."), while a
 * street named Post Office Rd starts with the house number — anchoring to the
 * start separates them. Both real examples on BIK-2135 lead with the POI name.
 *
 * The unambiguous needles above are NOT anchored: no street is named "FedEx".
 */
const POSTAL_SERVICE_LEADING_NEEDLES = ["post office", "postal service"];

/**
 * Accepts either a bare company string (the legacy Ship3 call shape) or a full
 * haystack object. A bare string is treated as a POI name.
 */
function splitHaystacks(
  address?: CarrierAddressHaystack | string | null
): { poi: string[]; street: string[] } {
  if (typeof address === "string") {
    const normalized = normalize(address);
    return { poi: normalized ? [normalized] : [], street: [] };
  }
  return { poi: poiHaystacks(address), street: streetHaystacks(address) };
}

export function isFedExOffice(
  address?: CarrierAddressHaystack | string | null
): boolean {
  const { poi, street } = splitHaystacks(address);
  return matchesAny([...poi, ...street], FEDEX_NEEDLES);
}

export function isUpsStore(
  address?: CarrierAddressHaystack | string | null
): boolean {
  const { poi, street } = splitHaystacks(address);
  return matchesAny([...poi, ...street], UPS_STORE_NEEDLES);
}

export function isPostalService(
  address?: CarrierAddressHaystack | string | null
): boolean {
  const { poi, street } = splitHaystacks(address);
  return (
    matchesAny([...poi, ...street], POSTAL_SERVICE_NEEDLES) ||
    startsWithAny(poi, POSTAL_SERVICE_LEADING_NEEDLES)
  );
}

/**
 * The blocking reason for an address, or null when it is shippable.
 *
 * Order matters: PO Box is resolved first so a "Post Office Box 9" street line
 * reports the PO Box reason rather than the post-office one.
 *
 * A UPS Store is deliberately absent — UPS delivers there, so it only warrants
 * a warning and must never block. (Never requested by Bill/Travis; preserved
 * from the pre-existing caller behavior — see BIK-2135.)
 */
export type CarrierRestriction = "poBox" | "fedEx" | "postalService";

export function getCarrierRestriction(
  address?: CarrierAddressHaystack | null
): CarrierRestriction | null {
  if (!address) {
    return null;
  }
  if (isPoBox(address)) {
    return "poBox";
  }
  if (isFedExOffice(address)) {
    return "fedEx";
  }
  if (isPostalService(address)) {
    return "postalService";
  }
  return null;
}

/**
 * BIK-2148: the non-blocking counterpart to getCarrierRestriction.
 *
 * A UPS Store is a valid UPS destination, so it must never block — which is why it is
 * absent from the restriction above. But being absent from the *blocking* helper also
 * left it out of the inline feedback that helper drives, so the only place it surfaced
 * was the alert raised on Search. That arrives too late to be useful: the page is
 * already in its loading state and the notice scrolls out of view.
 *
 * Kept as a separate function rather than a flag on getCarrierRestriction so the two
 * concerns stay apart at every call site: one answers "can I ship this at all", this one
 * answers "should I tell the user something about it".
 */
export type CarrierWarning = "upsStore";

export function getCarrierWarning(
  address?: CarrierAddressHaystack | null
): CarrierWarning | null {
  if (!address) {
    return null;
  }
  // Only when nothing blocks: a restricted address has its own, stronger message, and
  // showing both would bury it.
  if (getCarrierRestriction(address)) {
    return null;
  }
  return isUpsStore(address) ? "upsStore" : null;
}

const PO_BOX_PATTERNS = [
  /p\.?\s?o\.?\s?box/i,
  /post\s?office\s?box/i,
  /us\s?post\s?office/i,
  /united\s?states?\s?postal/i,
];

/** Leading "Box 123" — only meaningful at the start of a street line. */
const LEADING_BOX = /^box\s/i;

/**
 * A military "Box NNN" unit, anywhere in the line: "Unit 2050 Box 4190",
 * "PSC 802 Box 74", "CMR 415 Box 2100". Requires a number right after so real
 * street names survive — "Boxwood Lane", "Box Canyon Rd", "Mailbox Dr" have no
 * digits following and are left alone (\b also stops "Mailbox" from matching).
 */
const MILITARY_BOX = /\bbox\s*#?\s*\d/i;

/**
 * APO/FPO/DPO as a standalone token in the raw text. The city field is the
 * reliable signal, but on the Region paste path city is only populated after AVS
 * parses the address — and it may never be. Matching the pasted text means a
 * military address is flagged the moment it is entered.
 *
 * Anchored to word boundaries so it cannot fire inside another word.
 */
const MILITARY_CITY_TOKEN = /\b(apo|fpo|dpo)\b/i;

export function isPoBox(address?: CarrierAddressHaystack | null): boolean {
  if (!address) {
    return false;
  }

  const streetLike = [
    address.streetAddress,
    address.address1,
    address.address2,
    address.rawInput,
  ].filter((value): value is string => typeof value === "string" && !!value);

  for (const value of streetLike) {
    if (PO_BOX_PATTERNS.some((pattern) => pattern.test(value))) {
      return true;
    }
  }

  // "Box 123" as the very start of the street line (not "PO Box", which the
  // patterns above already cover, and not a mid-string "box").
  const street = address.streetAddress || address.address1;
  if (typeof street === "string" && LEADING_BOX.test(street.trim())) {
    return true;
  }

  // APO/FPO/DPO military addresses. The city field is checked first — it is the
  // unambiguous signal once AVS has parsed the address.
  const city = normalize(address.city);
  if (city === "apo" || city === "fpo" || city === "dpo") {
    return true;
  }

  // BIK-2135: on the Region paste path city stays empty until AVS parses the
  // one-liner (and may never fill), so a real APO like
  // "Unit 2050 Box 4190, APO AE 09204" would not be caught at entry time. Read
  // the raw text instead: an APO/FPO/DPO token plus a "Box NNN" unit. Both are
  // required together — "APO" alone could appear in a business name, and a
  // "Box NNN" alone is already covered by the PO Box patterns above.
  const rawText = [address.rawInput, address.streetAddress, address.address1]
    .filter((value): value is string => typeof value === "string" && !!value)
    .join(" ");
  return MILITARY_CITY_TOKEN.test(rawText) && MILITARY_BOX.test(rawText);
}
